import { getFirstShift } from 'server/shared/helpers/eligibilityChecks';
import { PoolerCustomerMap } from 'server/shared/helpers/PoolerMap';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { Job } from 'server/shared/models/job';
import { Shift } from 'server/shared/models/shift';
import { TranslationKeyTranslation } from 'server/shared/models/translation';
import { PoolerUser } from 'server/shared/models/user';
import { JobMatchPoolerUser } from '../../app/jobs/job-edit/job-edit.types';
import { getJobCustomerTariff } from '../getCustomerTariff';
import { getJobRestWarning } from '../getRestWarning';
import { getSecondsToJobStart } from '../getSecondsToJobStart';
import { getUserHasBlacklistedDepartment } from '../getUserHasBlacklistedJobDepartment';

export interface GetJobMatchPoolerParams {
  poolerUser: PoolerUser;
  job: Job;
  date: string;
  jobShifts: Shift[];
  department: Department;
  loggedInCustomer: Customer;
  poolerMap: PoolerCustomerMap;
  i18n: TranslationKeyTranslation;
}

export function getJobMatchPooler({
  poolerUser,
  job,
  date,
  jobShifts,
  department,
  loggedInCustomer,
  poolerMap,
  i18n,
}: GetJobMatchPoolerParams): JobMatchPoolerUser {
  if (!job || !poolerUser || !i18n)
    throw new Error('missing job, user or i18n');
  const customerOfPooler = poolerMap.getCustomerOfUser(poolerUser._id);
  if (!customerOfPooler) {
    throw new Error('no customer found for user ' + poolerUser._id);
  }
  const isUndecided = !poolerUser.availabilities.find(
    (availability) =>
      availability.date == date &&
      // has not decided availability for any of the shifts on this job:
      !jobShifts?.some(
        (jobShift) =>
          //  @ts-ignore
          !availability.shifts?.[jobShift.shift.toString()],
      ),
  );

  return {
    ...poolerUser,
    isUndecided,
    canBeBookedDirectly: isUndecided
      ? false
      : checkCanBookDirectly(
          customerOfPooler,
          department,
          date,
          jobShifts,
          loggedInCustomer,
        ),
    availabilityNote: poolerUser.availabilities.find(
      (availability) => availability.date === date,
    )?.note,
    isSharedPooler: customerOfPooler?._id !== loggedInCustomer?._id,
    sourceCustomer: {
      id: customerOfPooler._id,
      displayName: customerOfPooler.name,
      tariff: loggedInCustomer
        ? getJobCustomerTariff(
            loggedInCustomer,
            customerOfPooler._id,
            poolerUser?.certificates || [],
          )
        : '',
    },
    ...getJobRestWarning(
      {
        date,
        shifts: jobShifts,
        jobId: job._id,
        invitees: job.invitees,
      },
      poolerUser,
      i18n,
    ),
    ...getUserHasBlacklistedDepartment(job.department.id, poolerUser),
  };
}

function checkCanBookDirectly(
  customerOfPooler: Customer | undefined,
  department: Department | undefined,
  jobDate: string,
  jobShifts: Shift[],
  loggedInCustomer: Customer,
): boolean {
  if (!customerOfPooler || !jobShifts) {
    return false;
  }

  // Check if this is a shared pooler and if direct booking is prevented
  const isSharedPooler = customerOfPooler._id !== loggedInCustomer._id;
  if (
    isSharedPooler &&
    customerOfPooler.preventSharedPoolDirectAcceptCustomerIds &&
    customerOfPooler.preventSharedPoolDirectAcceptCustomerIds.includes(
      loggedInCustomer._id,
    )
  ) {
    return false;
  }

  let preventDirectAcceptWithinSecondsPerShift = 0;
  let firstJobShift = getFirstShift(jobShifts);

  if (
    department?.customerInformation?.[customerOfPooler?._id]
      ?.preventDirectAcceptWithinSecondsPerShift?.[firstJobShift.shift] !==
    undefined
  ) {
    preventDirectAcceptWithinSecondsPerShift =
      department.customerInformation?.[customerOfPooler._id]
        ?.preventDirectAcceptWithinSecondsPerShift?.[firstJobShift.shift] || 0;
  } else if (
    customerOfPooler?.preventDirectAcceptWithinSecondsPerShift?.[
      firstJobShift.shift
    ]
  ) {
    preventDirectAcceptWithinSecondsPerShift =
      customerOfPooler?.preventDirectAcceptWithinSecondsPerShift?.[
        firstJobShift.shift
      ] || 0;
  }
  if (preventDirectAcceptWithinSecondsPerShift && jobShifts) {
    if (
      preventDirectAcceptWithinSecondsPerShift &&
      preventDirectAcceptWithinSecondsPerShift >
        getSecondsToJobStart(jobDate, firstJobShift.from)
    ) {
      return false;
    }
  }
  return true;
}
